import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Card,
  CircularProgress,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useStyles } from "../../../theme/appStyles";
import { useNavigate } from "react-router";
import {
  useGetAllByPolicyNumberQuery,
  useGetCustomerRelationQuery,
  useGetCustomerQuery,
  useGetProductBrandQuery,
  useGetProductQuery,
} from "../../../Api/attoDeskApi";
import {
  ICustomerRelative2,
  ICustomer,
  IPolicy,
  IProductBrand,
  IProductPopUP,
  policyAllPrintTermsArray,
  policyTermsArray,
  ICustomerRelative,
} from "../../../Api/Interface/api.interface";
import dayjs, { Dayjs } from "dayjs";
import { useParams } from "react-router-dom";
import ReactToPrint from "react-to-print";
import LocalPrintshopSharpIcon from "@mui/icons-material/LocalPrintshopSharp";
import LogoMain from "../../../Images/logo.png";
import { formatPhoneNumberForDisplay } from "../../../Components/Validation/validationUtils";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { styled } from "@mui/material/styles";
import { GlobalStyles } from "@mui/material";
import { formatNumber } from "../../../Components/formatNumber/formatNumber";
import { Link } from "react-router-dom";

const capitalizeFirstLetter = (name: string | null | undefined) => {
  if (!name) return "";
  return name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();
};

// const PrintContainer = styled("div")({
//   "@media print": {
//     pageBreakInside: "avoid",

//     // table: {
//     //   borderCollapse: "collapse",
//     //   width: "100%",
//     // },
//     // "th, td": {
//     //   border: "1px solid #767c83 !important",
//     //   padding: "12px !important",
//     //   color: "#000 !important",
//     // },
//     // "thead th": {
//     //   backgroundColor: "#dadee5 !important",
//     // },
//     // "table *": {
//     //   WebkitPrintColorAdjust: "exact",
//     //   printColorAdjust: "exact",
//     // },
//   },
//   "@page": {
//     marginTop: "10px",
//     marginBottom: "10px",
//     // paddingTop: "300px",
//     // paddingBottom: "300px",
//   },
// });

const PrintContainer = styled("div")({
  "@page": {
    paddingTop: "30px",
    paddingBottom: "30px",
  },
});

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const theme = useTheme();
  const { customerId, policyNumber } = useParams<{
    customerId: string;
    policyNumber: string;
  }>();

  const componentRef = useRef<HTMLDivElement>(null);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { data, isLoading, isError } = useGetCustomerQuery({
    searchText: "",
    id: String(customerId),
  });

  const {
    data: customerProjectData,
    isLoading: customerProjectDataIsLoading,
    refetch: cutomerProjectRefetch,
    isError: cutomerProjectisError,
  } = useGetAllByPolicyNumberQuery({
    searchText: "",
    id: "",
    policyCustomerId: String(customerId),
    policyNumber: String(policyNumber),
  });

  const { data: brandListData, isLoading: brandIsLoading } =
    useGetProductBrandQuery({
      searchText: "",
      isActive: true,
    });

  const { data: customerFullData, isLoading: customerFullDataISLoading } =
    useGetCustomerQuery({
      searchText: "",
      id: "",
    });

  const {
    data: productData,
    isLoading: productDataIsLoading,
    isError: productDataError,
  } = useGetProductQuery({
    searchText: "",
    id: "",
    productBrandId: null,
    productCategoryId: null,
    productTagIds: null,
    productViewOnline: null,
    createdDateStart: null,
    createdDateEnd: null,
    createdDate: null,
  });

  const policyCustomerId = String(customerId || "");

  const {
    data: relativeData,
    isLoading: relativeIsLoading,
    isError: relativeError,
  } = useGetCustomerRelationQuery(
    { policyCustomerId },
    { skip: !policyCustomerId }
  );

  const customer = useMemo(() => {
    if (data && Array.isArray(data.data) && data.data.length > 0) {
      return data.data[0] as ICustomer;
    }
    return null;
  }, [data]);

  const project: IPolicy[] = useMemo(() => {
    if (customerProjectData && Array.isArray(customerProjectData.data)) {
      return customerProjectData.data as IPolicy[];
    }
    return [];
  }, [customerProjectData]);

  const relationDataList = useMemo(() => {
    return (relativeData?.data ?? []) as ICustomerRelative2[];
  }, [relativeData?.data]);

  const relativeList = useMemo(() => {
    return relativeData?.data as ICustomerRelative[];
  }, [relativeData?.data]);

  const brandList = useMemo(() => {
    return brandListData?.data as IProductBrand[];
  }, [brandListData?.data]);

  const customerAllList = useMemo(() => {
    return customerFullData?.data as ICustomer[];
  }, [customerFullData?.data]);

  const productList = useMemo(() => {
    return productData?.data as IProductPopUP[];
  }, [productData?.data]);

  const brandMap = useMemo(() => {
    const map = new Map();
    brandList?.forEach((category) => {
      map.set(category.id, category.productBrandName);
    });
    return map;
  }, [brandList]);

  const relativeMap = useMemo(() => {
    const map = new Map();
    relationDataList?.forEach((item) => {
      map.set(item.id, `${toProperCase(item?.related_customer_name)}`);
    });
    return map;
  }, [relationDataList]);

  const productMap = useMemo(() => {
    const map = new Map();
    productList?.forEach((brand) => {
      map.set(brand.id, brand.productName);
    });
    return map;
  }, [productList]);

  const customerMap = useMemo(() => {
    const map = new Map();
    customerAllList?.forEach((category) => {
      map.set(
        category.id,
        capitalizeFirstLetter(category.customerFirstName) +
          " " +
          capitalizeFirstLetter(category.customerLastName)
      );
    });
    return map;
  }, [customerAllList]);

  const firstPolicy = project[0] || {};

  const today = dayjs();
  const formattedDate = dayjs(today).format("MMM D, YYYY");

  const formatAddress = (
    customerAddresLine: string,
    customerCity: string,
    customerProvince: string,
    customerPostalCode: string
  ) => {
    return `${capitalizeFirstLetter(
      customerAddresLine
    )} ${capitalizeFirstLetter(customerCity)},\n${capitalizeFirstLetter(
      customerProvince
    )} ${capitalizeFirstLetter(customerPostalCode)}`;
  };

  if (
    isLoading ||
    productDataIsLoading ||
    customerProjectDataIsLoading ||
    brandIsLoading ||
    customerFullDataISLoading ||
    relativeIsLoading
  )
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="10vh"
      >
        <CircularProgress />
      </Box>
    );

  return (
    <div>
      <Grid container spacing={2}>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.quotesCardInvoice}>
            <Grid container spacing={1} px={2} py={2}>
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Grid
                  container
                  spacing={1}
                  justifyContent="flex-end"
                  sx={{ display: "flex" }}
                >
                  <Grid item>
                    <Link
                      to={`/coverage-report/customer/${customerId}/policy/${String(
                        policyNumber
                      )}`}
                      className={classes.iconLinkPropNormal}
                    >
                      <Button
                        // variant="outlined"
                        className={classes.addNewButton}
                        onClick={() =>
                          navigate(
                            `/coverage-report/customer/${customerId}/policy/${String(
                              policyNumber
                            )}`
                          )
                        }
                      >
                        Type 1 pdf
                      </Button>
                    </Link>
                  </Grid>
                  <Grid item>
                    <Button
                      variant="outlined"
                      startIcon={<ArrowBackIcon />}
                      className={classes.addButton}
                      onClick={() => navigate(-1)}
                    >
                      Back
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </Grid>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <PrintContainer ref={componentRef}>
            <Card className={classes.quotesCardInvoice}>
              <Grid container spacing={1} padding={"2px"}>
                <Grid
                  item
                  lg={12}
                  md={12}
                  sm={12}
                  xs={12}
                  className={classes.titleGrid}
                >
                  <Grid
                    container
                    className={classes.titleGridInvoice}
                    justifyContent={"space-between"}
                    padding={3}
                  >
                    <Grid item lg={12} md={12} sm={12} xs={12} pb={2}>
                      <Grid container spacing={1}>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          display="flex"
                          justifyContent="flex-end"
                          alignItems="center"
                        >
                          <img
                            src={LogoMain}
                            alt="AttoDesk Login"
                            style={{
                              width: "250px",
                              height: "auto",
                              display: "block",
                            }}
                          />
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12} pb={3}>
                          <Typography className={classes.bodyPrint1}>
                            <strong>Date :&nbsp;&nbsp;</strong> {formattedDate}
                          </Typography>
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12} pb={3}>
                          {customer &&
                            (customer.customerFirstName ||
                              customer.customerLastName) && (
                              <Typography
                                className={classes.bodyPrintHeading}
                                sx={{
                                  textTransform: "uppercase",
                                  color: "black",
                                }}
                              >
                                {(customer?.customerFirstName || "") +
                                  " " +
                                  (customer?.customerLastName || "")}
                              </Typography>
                            )}
                          {customer &&
                            (customer.customerAddresLine ||
                              customer.customerCity) && (
                              <Typography
                                className={classes.bodyPrintHeading}
                                sx={{
                                  textTransform: "uppercase",
                                  color: "black",
                                }}
                              >
                                {(customer?.customerAddresLine || "") +
                                  " " +
                                  (customer?.customerCity || "")}
                              </Typography>
                            )}
                          {customer &&
                            (customer.customerProvince ||
                              customer.customerPostalCode) && (
                              <Typography
                                className={classes.bodyPrintHeading}
                                sx={{
                                  textTransform: "uppercase",
                                  color: "black",
                                }}
                              >
                                {(customer?.customerProvince || "") +
                                  " " +
                                  (customer?.customerPostalCode || "")}
                              </Typography>
                            )}
                        </Grid>
                        <Grid item lg={12} md={12} sm={12} xs={12} pb={3}>
                          {customer && customer.customerHomeMobileNum && (
                            <Typography className={classes.bodyPrint1}>
                              <strong>Home Phone :&nbsp;&nbsp;</strong>{" "}
                              {formatPhoneNumberForDisplay(
                                customer.customerHomeMobileNum
                              )}
                            </Typography>
                          )}
                          {customer && customer.customerMobileNum && (
                            <Typography className={classes.bodyPrint1}>
                              <strong>Cell Phone :&nbsp;&nbsp;</strong>{" "}
                              {formatPhoneNumberForDisplay(
                                customer.customerMobileNum
                              )}
                            </Typography>
                          )}
                          {customer && customer.customerWorkMobileNum && (
                            <Typography className={classes.bodyPrint1}>
                              <strong>Work Phone :&nbsp;&nbsp;</strong>{" "}
                              {formatPhoneNumberForDisplay(
                                customer.customerWorkMobileNum
                              )}
                            </Typography>
                          )}
                          {customer && customer.customerEmail && (
                            <Typography className={classes.bodyPrint1}>
                              <strong>Email :&nbsp;&nbsp;</strong>{" "}
                              {customer.customerEmail?.toLowerCase()}
                            </Typography>
                          )}
                        </Grid>

                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          className={classes.policySummaryBox}
                          textAlign={"center"}
                          justifyContent={"center"}
                          mb={5}
                        >
                          <Typography className={classes.policySummaryTitile}>
                            Policy Summaries
                          </Typography>
                        </Grid>

                        <Grid item lg={6} md={6} sm={6} xs={6} pt={5}>
                          {firstPolicy.policyNumber && (
                            <Typography className={classes.bodyPrint1}>
                              <strong>Policy Number :&nbsp;&nbsp;</strong>{" "}
                              {firstPolicy.policyNumber || " "}
                            </Typography>
                          )}
                          {firstPolicy.policyInsuranceCompanyId && (
                            <Typography className={classes.bodyPrint1}>
                              <strong>Company Name :&nbsp;&nbsp;</strong>{" "}
                              {brandMap.get(
                                Number(firstPolicy.policyInsuranceCompanyId)
                              ) || ""}
                            </Typography>
                          )}
                          {firstPolicy.policyIssueDate && (
                            <Typography className={classes.bodyPrint1}>
                              <strong>Issue Date :&nbsp;&nbsp;</strong>{" "}
                              {firstPolicy.policyIssueDate
                                ? dayjs(firstPolicy.policyIssueDate).format(
                                    "MMM D, YYYY"
                                  )
                                : ""}
                            </Typography>
                          )}
                        </Grid>
                        <Grid item lg={6} md={6} sm={6} xs={6} pt={5}>
                          {firstPolicy.policyPayor && (
                            <Typography className={classes.bodyPrint1}>
                              <strong>Payor Name :&nbsp;&nbsp;</strong>{" "}
                              {firstPolicy.policyPayor || " "}
                            </Typography>
                          )}
                          {firstPolicy.policyDrawDay && (
                            <Typography className={classes.bodyPrint1}>
                              <strong>Draw Day :&nbsp;&nbsp;</strong>{" "}
                              {firstPolicy.policyDrawDay || " "}
                            </Typography>
                          )}
                        </Grid>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          mt={5}
                          className={classes.titleGrid}
                        >
                          <Grid container spacing={2}>
                            <Grid item lg={12} md={12} sm={12} xs={12}>
                              <TableContainer>
                                <Table className={classes.tableReport}>
                                  <TableHead
                                    className={classes.tableHeaderRowReport}
                                  >
                                    <TableRow>
                                      <TableCell
                                        className={classes.tablheaderReport}
                                        sx={{
                                          // textAlign: "center",
                                          width: "60%",
                                        }}
                                      >
                                        <Typography
                                          className={classes.bodyPrint1}
                                        >
                                          <strong>Details</strong>
                                        </Typography>
                                      </TableCell>
                                      <TableCell
                                        className={classes.tablheaderReport}
                                        sx={{
                                          textAlign: "right",
                                          width: "20%",
                                        }}
                                      >
                                        <Typography
                                          className={classes.bodyPrint1}
                                        >
                                          <strong>Coverage Amount </strong>
                                        </Typography>
                                      </TableCell>
                                      <TableCell
                                        className={classes.tablheaderReport}
                                        sx={{
                                          textAlign: "right",
                                          width: "20%",
                                        }}
                                      >
                                        <Typography
                                          className={classes.bodyPrint1}
                                        >
                                          <strong>Premium</strong>
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {cutomerProjectisError ||
                                    project.length === 0 ? (
                                      <TableRow>
                                        <TableCell colSpan={8} align="center">
                                          {cutomerProjectisError
                                            ? "Error fetching data"
                                            : "No data available"}
                                        </TableCell>
                                      </TableRow>
                                    ) : (
                                      project.map((policy, index) => (
                                        <TableRow key={policy.id}>
                                          <TableCell
                                            className={classes.tablCellReport}
                                            sx={{
                                              width: "60%",
                                            }}
                                          >
                                            {policy.policyProductId && (
                                              <Typography
                                                className={
                                                  classes.bodyPrintHeading
                                                }
                                                sx={{
                                                  textTransform: "uppercase",
                                                  color: "black",
                                                  fontWeight: "400 !important",
                                                }}
                                              >
                                                {productMap.get(
                                                  Number(policy.policyProductId)
                                                ) || ""}
                                              </Typography>
                                            )}
                                            {policy.policyInsuredDetails &&
                                              policy.policyInsuredDetails
                                                .length > 0 && (
                                                <Typography
                                                  className={classes.bodyPrint1}
                                                >
                                                  <span
                                                    style={{ fontWeight: 510 }}
                                                  >
                                                    Insured :&nbsp;&nbsp;
                                                  </span>{" "}
                                                  <strong> </strong>{" "}
                                                  {policy.policyInsuredDetails.map(
                                                    (insuredId, index) => {
                                                      const customerName =
                                                        relativeMap.get(
                                                          Number(insuredId)
                                                        ) || "";

                                                      return (
                                                        <span key={insuredId}>
                                                          {customerName}
                                                          {index <
                                                            policy
                                                              .policyInsuredDetails
                                                              .length -
                                                              1 && ", "}
                                                        </span>
                                                      );
                                                    }
                                                  )}
                                                </Typography>
                                              )}
                                            {(policy.policyIsLifeTimeExpiryDate ||
                                              policy.policyExpiryDate) && (
                                              <Typography
                                                className={classes.bodyPrint1}
                                              >
                                                {/* Expires :&nbsp;&nbsp; */}
                                                <span
                                                  style={{ fontWeight: 510 }}
                                                >
                                                  Expires :&nbsp;&nbsp;
                                                </span>{" "}
                                                {policy.policyIsLifeTimeExpiryDate
                                                  ? "Death"
                                                  : policy.policyExpiryDate
                                                  ? dayjs(
                                                      policy.policyExpiryDate
                                                    ).format("MMM D, YYYY")
                                                  : ""}
                                              </Typography>
                                            )}

                                            {policy.policyPrimaryBenificariesNew &&
                                              policy
                                                .policyPrimaryBenificariesNew
                                                .length > 0 && (
                                                <Typography
                                                  className={classes.bodyPrint1}
                                                >
                                                  {/* Beneficiaries :&nbsp;&nbsp; */}
                                                  <strong>
                                                    Beneficiaries :&nbsp;&nbsp;
                                                  </strong>{" "}
                                                  {/* {policy.policyPrimaryBenificariesNew.map(
                                                  (insured, index) => (
                                                    <span key={index}>
                                                      {toProperCase(
                                                        insured.name
                                                      )}{" "}
                                                      {insured.payoutPercentage
                                                        ? `- ${insured.payoutPercentage}%`
                                                        : ""}
                                                      {index <
                                                        policy
                                                          .policyPrimaryBenificariesNew
                                                          .length -
                                                          1 && ", "}
                                                    </span>
                                                  )
                                                )} */}
                                                  {policy.policyPrimaryBenificariesNew.map(
                                                    (insuredId, index) => {
                                                      const customerName =
                                                        relativeMap.get(
                                                          Number(
                                                            insuredId.column1
                                                          )
                                                        ) || "";

                                                      return (
                                                        <span key={index}>
                                                          {customerName}
                                                          {insuredId.payoutPercentage
                                                            ? `- ${insuredId.payoutPercentage}%`
                                                            : ""}
                                                          {/* {insuredId.column2
                                                              ? ` (${insuredId.column2})`
                                                              : ""} */}
                                                          {index <
                                                            policy
                                                              .policyPrimaryBenificariesNew
                                                              .length -
                                                              1 && ", "}
                                                        </span>
                                                      );
                                                    }
                                                  )}
                                                </Typography>
                                              )}
                                            {firstPolicy.policyRemark && (
                                              <Typography
                                                className={classes.bodyPrint1}
                                              >
                                                <strong>Remarks:</strong>{" "}
                                                <Typography
                                                  className={classes.bodyPrint1}
                                                  dangerouslySetInnerHTML={{
                                                    __html: policy.policyRemark,
                                                  }}
                                                />
                                                {/* {policy.policyRemark || ""} */}
                                              </Typography>
                                            )}
                                          </TableCell>
                                          <TableCell
                                            className={classes.tablCellReport}
                                            sx={{
                                              textAlign: "right",
                                              width: "20%",
                                              verticalAlign: "top",
                                            }}
                                          >
                                            {policy.policyCoverageAmount && (
                                              <Typography
                                                className={
                                                  classes.bodyPrintHeading
                                                }
                                                sx={{
                                                  textTransform: "uppercase",
                                                  color: "black",
                                                }}
                                              >
                                                {formatNumber(
                                                  policy.policyCoverageAmount ||
                                                    "0.00"
                                                )}
                                              </Typography>
                                            )}
                                          </TableCell>
                                          <TableCell
                                            className={classes.tablCellReport}
                                            sx={{
                                              textAlign: "right",
                                              width: "20%",
                                              verticalAlign: "top",
                                            }}
                                          >
                                            {policy.policyPremiumDetails && (
                                              <Typography
                                                className={
                                                  classes.bodyPrintHeading
                                                }
                                                sx={{
                                                  textTransform: "uppercase",
                                                  color: "black",
                                                }}
                                              >
                                                {formatNumber(
                                                  policy.policyPremiumDetails ||
                                                    "0.00"
                                                )}
                                              </Typography>
                                            )}
                                          </TableCell>
                                        </TableRow>
                                      ))
                                    )}
                                    <TableRow>
                                      <TableCell
                                        className={classes.tablCellReport}
                                        sx={{
                                          width: "60%",
                                        }}
                                      >
                                        <Typography
                                          className={classes.bodyPrint1}
                                        >
                                          <strong>
                                            Total for Policy (including policy
                                            fee)
                                          </strong>{" "}
                                        </Typography>
                                      </TableCell>
                                      <TableCell
                                        className={classes.tablCellReport}
                                        sx={{
                                          textAlign: "right",
                                          width: "20%",
                                          verticalAlign: "top",
                                        }}
                                      >
                                        <Typography
                                          className={classes.bodyPrintHeading}
                                          sx={{
                                            textTransform: "uppercase",
                                            color: "black",
                                          }}
                                        >
                                          {" "}
                                        </Typography>
                                      </TableCell>
                                      <TableCell
                                        className={classes.tablCellReport}
                                        sx={{
                                          textAlign: "right",
                                          width: "20%",
                                          verticalAlign: "top",
                                        }}
                                      >
                                        <Typography
                                          className={classes.bodyPrintHeading}
                                          sx={{
                                            textTransform: "uppercase",
                                            color: "black",
                                          }}
                                        >
                                          {formatNumber(
                                            firstPolicy.policyPremium || "0.00"
                                          )}
                                        </Typography>
                                      </TableCell>
                                    </TableRow>
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Grid>
                          </Grid>
                        </Grid>
                        <Grid
                          item
                          lg={12}
                          md={12}
                          sm={12}
                          xs={12}
                          mt={5}
                          // sx={{
                          //   "@media print": {
                          //     pageBreakBefore: "always", // Forces this container to start on a new page
                          //     breakBefore: "always", // Modern equivalent for the same functionality
                          //   },
                          // }}
                        >
                          <Typography
                            className={classes.bodyPrint1}
                            sx={{ textDecoration: "underline" }}
                          >
                            <strong>Additional Comments</strong>{" "}
                          </Typography>
                          {project.map((policy, index) => {
                            return (
                              <Grid key={policy.id} container>
                                {(policy.policyTermsArray.length > 0 ||
                                  policy.policySummaryReportArray.length >
                                    0) && (
                                  <Grid item lg={12} md={12} sm={12} xs={12}>
                                    <Typography
                                      className={classes.bodyPrint1}
                                      pt={2}
                                    >
                                      <strong>
                                        {productMap.get(
                                          Number(policy.policyProductId)
                                        ) || ""}
                                      </strong>{" "}
                                    </Typography>
                                  </Grid>
                                )}

                                {policy.policyTermsArray &&
                                  policy.policyTermsArray.filter(
                                    (item) => item.isReport
                                  ).length > 0 && (
                                    <Grid container alignItems="center">
                                      <Grid
                                        container
                                        spacing={2}
                                        alignItems="center"
                                        padding={1}
                                      >
                                        {policy.policyTermsArray
                                          .filter((item) => item.isReport)
                                          .map(
                                            (
                                              item: policyAllPrintTermsArray,
                                              idx: number
                                            ) => (
                                              <Grid
                                                item
                                                lg={12}
                                                md={12}
                                                sm={12}
                                                xs={12}
                                                key={idx}
                                                sx={{
                                                  "@media print": {
                                                    pageBreakInside: "avoid",
                                                  },
                                                }}
                                              >
                                                <Grid
                                                  container
                                                  spacing={2}
                                                  alignItems="center"
                                                >
                                                  <Grid
                                                    item
                                                    lg={0.5}
                                                    md={0.5}
                                                    sm={0.5}
                                                    xs={0.5}
                                                    display="flex"
                                                    justifyContent="flex-start"
                                                    alignItems="flex-start"
                                                  >
                                                    <Typography
                                                      sx={{
                                                        fontSize: 13,
                                                        display: "flex",
                                                        justifyContent:
                                                          "center",
                                                        alignItems: "center",
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: "50%",
                                                        border: "1px solid ",
                                                        textAlign: "center",
                                                        lineHeight: "13px",
                                                        color: "black",
                                                      }}
                                                    >
                                                      {"✔"}
                                                    </Typography>
                                                  </Grid>
                                                  <Grid
                                                    item
                                                    lg={11.5}
                                                    md={11.5}
                                                    sm={11.5}
                                                    xs={11.5}
                                                  >
                                                    {/* <Typography
                                                      className="headingSummary"
                                                      sx={{
                                                        pb: "2px",
                                                        color: "black",
                                                      }}
                                                    >
                                                      {
                                                        item.policyTermsDescription
                                                      }
                                                    </Typography> */}
                                                    <Typography
                                                      className={
                                                        classes.bodyPrint133
                                                      }
                                                      component="div"
                                                      dangerouslySetInnerHTML={{
                                                        __html:
                                                          item.policyTermsDescription,
                                                      }}
                                                    />
                                                  </Grid>
                                                </Grid>
                                              </Grid>
                                            )
                                          )}
                                      </Grid>
                                    </Grid>
                                  )}

                                {policy.policySummaryReportArray &&
                                  policy.policySummaryReportArray.filter(
                                    (item) => item.isPolicyTermsSummaryReport
                                  ).length > 0 && (
                                    <Grid container alignItems="center">
                                      <Grid
                                        container
                                        spacing={2}
                                        alignItems="center"
                                        padding={1}
                                      >
                                        {policy.policySummaryReportArray
                                          .filter(
                                            (item) =>
                                              item.isPolicyTermsSummaryReport
                                          )
                                          .map(
                                            (
                                              item: policyTermsArray,
                                              idx: number
                                            ) => (
                                              <Grid
                                                item
                                                lg={12}
                                                md={12}
                                                sm={12}
                                                xs={12}
                                                key={idx}
                                              >
                                                <Grid
                                                  container
                                                  spacing={2}
                                                  alignItems="center"
                                                >
                                                  <Grid
                                                    item
                                                    lg={0.5}
                                                    md={0.5}
                                                    sm={0.5}
                                                    xs={0.5}
                                                    display="flex"
                                                    justifyContent="flex-start"
                                                    alignItems="flex-start"
                                                  >
                                                    <Typography
                                                      sx={{
                                                        fontSize: 13,
                                                        display: "flex",
                                                        justifyContent:
                                                          "center",
                                                        alignItems: "center",
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: "50%",
                                                        border: "1px solid ",
                                                        textAlign: "center",
                                                        lineHeight: "13px",
                                                        color: "black",
                                                      }}
                                                    >
                                                      {"✔"}
                                                    </Typography>
                                                  </Grid>
                                                  <Grid
                                                    item
                                                    lg={11.5}
                                                    md={11.5}
                                                    sm={11.5}
                                                    xs={11.5}
                                                  >
                                                    <Typography
                                                      className={
                                                        classes.bodyPrint133
                                                      }
                                                      component="div"
                                                      //   dangerouslySetInnerHTML={{
                                                      //     __html:
                                                      //       item.policyTermsDescription,
                                                      //   }}

                                                      dangerouslySetInnerHTML={{
                                                        __html: `${item.policyTermsDescription}`,
                                                      }}
                                                    />
                                                    {/* <Typography
                                                      className="headingSummary"
                                                      sx={{
                                                        pb: "2px",
                                                        color: "black",
                                                      }}
                                                    >
                                                      {
                                                        item.policyTermsDescription
                                                      }
                                                    </Typography> */}
                                                  </Grid>
                                                </Grid>
                                              </Grid>
                                            )
                                          )}
                                      </Grid>
                                    </Grid>
                                  )}
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Card>
          </PrintContainer>
        </Grid>
        <Grid item lg={12} md={12} sm={12} xs={12}>
          <Card className={classes.quotesCardInvoice}>
            <Grid container spacing={1} padding={"2px"}>
              <Grid
                item
                lg={12}
                md={12}
                sm={12}
                xs={12}
                sx={{ display: "flex", justifyContent: "flex-end" }}
              >
                <Grid
                  container
                  spacing={1}
                  justifyContent="flex-end"
                  sx={{ display: "flex" }}
                >
                  <Grid item>
                    <ReactToPrint
                      trigger={() => (
                        <Button
                          variant="contained"
                          startIcon={<LocalPrintshopSharpIcon />}
                          className={classes.addButton}
                        >
                          Print
                        </Button>
                      )}
                      content={() => componentRef.current}
                      pageStyle={`
                      @page {
                        margin: 5mm 1mm 10mm 1mm;

                        @top-right-corner {
                            content: "  ";
                          }
                        @bottom-right {
                          content: "Page " counter(page);
                        }
                      }
                        @media print {
                          body {
                            margin: 0;
                            padding: 0;
                          }
                    
                          table {
                            margin: 0 !important;
                            padding: 0 !important;
                            border-collapse: collapse !important;
                          }
                    
                          th, td {
                            padding: 4px !important; /* Optional: you can control cell spacing here */
                          }
                        }
                      `}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Card>
        </Grid>
      </Grid>
    </div>
  );
};

export default Index;
